import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClientCard = ({ client, onAddMinutes, onSubtractMinutes }) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const getBalanceColor = (balance) => {
    if (balance === 0) return 'text-muted-foreground';
    if (balance <= 30) return 'text-warning';
    if (balance <= 60) return 'text-accent';
    return 'text-success';
  };

  const getBalanceIcon = (balance) => {
    if (balance === 0) return 'AlertCircle';
    if (balance <= 30) return 'Clock';
    return 'CheckCircle';
  };

  const handleCardClick = () => {
    navigate(`/client-detail/${client.id}`);
  };

  const handleQuickAction = async (action, amount) => {
    setIsProcessing(true);
    try {
      if (action === 'add') {
        await onAddMinutes(client.id, amount);
      } else {
        await onSubtractMinutes(client.id, amount);
      }
    } catch (error) {
      console.error('Quick action error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'No phone number';
    return phone.replace(/\D/g, '');
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      {/* Main Card Content - Clickable */}
      <div 
        className="p-6 cursor-pointer hover:bg-muted/30 transition-colors duration-150"
        onClick={handleCardClick}
      >
        {/* Client Info Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground truncate mb-1">
              {client.name}
            </h3>
            <p className="text-sm text-muted-foreground flex items-center">
              <Icon name="Phone" size={14} className="mr-1.5 flex-shrink-0" />
              {formatPhoneNumber(client.phone)}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0">
            <Icon 
              name={getBalanceIcon(client.balance)} 
              size={20} 
              className={getBalanceColor(client.balance)}
            />
          </div>
        </div>

        {/* Balance / Sessions Display */}
        {(client.balance > 0 || client.sessions > 0) && (
          <div className="bg-muted/50 rounded-lg p-4 mb-4 space-y-2">
            {client.balance > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Total Minutes
                </span>
                <div className="text-right">
                  <div className={`text-2xl font-bold font-mono ${getBalanceColor(client.balance)}`}>
                    {client.balance}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    minutes
                  </div>
                </div>
              </div>
            )}

            {client.sessions > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Sessions
                </span>
                <div className="text-right">
                  <div className="text-lg font-semibold font-mono text-foreground">
                    {client.sessions}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    sessions
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Last Activity */}
        {client.lastActivity && (
          <div className="flex items-center text-xs text-muted-foreground">
            <Icon name="Clock" size={12} className="mr-1" />
            Last activity: {new Date(client.lastActivity).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Quick Actions Footer */}
      <div className="px-6 py-4 bg-muted/20 border-t border-border">
        <div className="flex items-center justify-between space-x-2">
          <div className="flex space-x-2 flex-1">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleQuickAction('add', 30);
              }}
              loading={isProcessing}
              iconName="Plus"
              iconPosition="left"
              iconSize={14}
              className="flex-1 text-xs"
            >
              +30 min
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleQuickAction('subtract', 15);
              }}
              loading={isProcessing}
              disabled={client.balance < 15}
              iconName="Minus"
              iconPosition="left"
              iconSize={14}
              className="flex-1 text-xs"
            >
              -15 min
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCardClick}
            iconName="ArrowRight"
            iconSize={14}
            className="px-3"
            title="View details"
          />
        </div>
      </div>
    </div>
  );
};

export default ClientCard;